/**
 * AddSectionToolbar + PresetWizard — Sprint M
 *
 * Left-hand toolbox for adding new blocks to the canvas. Each button seeds a
 * default section (name, capacity, geometry) which the parent then patches
 * onto its local state and PATCHes to the backend via the debounced save.
 */
import React, { useState } from "react";
import {
  Square, Triangle, MapPin as StageIcon, DoorOpen, Radio, Music, Video, Sparkles, X,
} from "lucide-react";

const _iconFor = (t) => {
  if (t === "stage") return StageIcon;
  if (t === "entrance") return DoorOpen;
  if (t === "broadcast") return Radio;
  if (t === "sound") return Music;
  if (t === "camera") return Video;
  return Square;
};

const DECOR_PRESETS = [
  { name: "STAGE", icon: "stage", h: 8, w: 60, x: 20, y: 6 },
  { name: "MAIN ENTRANCE", icon: "entrance", h: 6, w: 40, x: 30, y: 88 },
  { name: "BROADCASTING", icon: "broadcast", h: 5, w: 24, x: 3, y: 88 },
  { name: "SOUND DESK", icon: "sound", h: 5, w: 20, x: 73, y: 88 },
  { name: "CAMERA", icon: "camera", h: 5, w: 12, x: 44, y: 4 },
];

export function AddSectionToolbar({ disabled, onAdd }) {
  return (
    <div className="border border-[var(--gt-line)] rounded-sm bg-white p-3 space-y-3" data-testid="add-section-toolbar">
      <div className="gt-label">Add block</div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAdd?.({
            name: "New Section",
            section_type: "general",
            capacity: 40,
            shape_type: "rect",
            x_percent: 30, y_percent: 40,
            width_percent: 24, height_percent: 12,
            is_decorative: false,
          })}
          className="gt-btn-secondary text-xs"
          data-testid="add-rect-btn"
        >
          <Square size={12} /> Rectangle
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAdd?.({
            name: "New Wedge",
            section_type: "general",
            capacity: 30,
            shape_type: "wedge",
            x_percent: 30, y_percent: 40,
            width_percent: 24, height_percent: 12,
            is_decorative: false,
          })}
          className="gt-btn-secondary text-xs"
          data-testid="add-wedge-btn"
        >
          <Triangle size={12} /> Wedge
        </button>
      </div>

      <div className="gt-label pt-2 border-t border-[var(--gt-line)]">Decorative</div>
      <div className="grid grid-cols-2 gap-2">
        {DECOR_PRESETS.map((d) => {
          const Icon = _iconFor(d.icon);
          return (
            <button
              key={d.name}
              type="button"
              disabled={disabled}
              onClick={() => onAdd?.({
                name: d.name,
                section_type: d.icon,
                capacity: 0,
                shape_type: "rect",
                x_percent: d.x, y_percent: d.y,
                width_percent: d.w, height_percent: d.h,
                is_decorative: true,
                icon_name: d.icon,
                label_display: "always",
              })}
              className="gt-btn-secondary text-[10px] text-left"
              data-testid={`add-decor-${d.icon}-btn`}
            >
              <Icon size={11} /> {d.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// PresetWizard — appears when the template is empty, offers 4 starters
// ------------------------------------------------------------------
const PRESETS = [
  {
    id: "theatre",
    label: "Theatre",
    desc: "Stage top, symmetric stalls, balcony overhead",
    sections: [
      { name: "STAGE", capacity: 0, is_decorative: true, section_type: "stage", x_percent: 15, y_percent: 6, width_percent: 70, height_percent: 8, label_display: "always" },
      { name: "Premium", capacity: 40, x_percent: 15, y_percent: 20, width_percent: 70, height_percent: 12 },
      { name: "Stalls Left", capacity: 30, x_percent: 15, y_percent: 34, width_percent: 32, height_percent: 14 },
      { name: "Stalls Right", capacity: 30, x_percent: 53, y_percent: 34, width_percent: 32, height_percent: 14 },
      { name: "Balcony", capacity: 50, x_percent: 15, y_percent: 62, width_percent: 70, height_percent: 12 },
      { name: "MAIN ENTRANCE", capacity: 0, is_decorative: true, section_type: "entrance", x_percent: 30, y_percent: 88, width_percent: 40, height_percent: 6, label_display: "always" },
    ],
  },
  {
    id: "arena",
    label: "Arena / Bowl",
    desc: "Field at centre, four wedge stands",
    sections: [
      { name: "North Stand", capacity: 100, x_percent: 20, y_percent: 6, width_percent: 60, height_percent: 15 },
      { name: "South Stand", capacity: 100, x_percent: 20, y_percent: 79, width_percent: 60, height_percent: 15 },
      { name: "East Stand", capacity: 80, x_percent: 78, y_percent: 25, width_percent: 15, height_percent: 50 },
      { name: "West Stand", capacity: 80, x_percent: 7, y_percent: 25, width_percent: 15, height_percent: 50 },
      { name: "FIELD", capacity: 0, is_decorative: true, section_type: "stage", x_percent: 25, y_percent: 25, width_percent: 50, height_percent: 50, label_display: "always" },
    ],
  },
  {
    id: "cabaret",
    label: "Cabaret Tables",
    desc: "Small stage + informal tables (grid)",
    sections: [
      { name: "STAGE", capacity: 0, is_decorative: true, section_type: "stage", x_percent: 30, y_percent: 6, width_percent: 40, height_percent: 8, label_display: "always" },
      { name: "Table Zone A", capacity: 40, x_percent: 10, y_percent: 22, width_percent: 38, height_percent: 30 },
      { name: "Table Zone B", capacity: 40, x_percent: 52, y_percent: 22, width_percent: 38, height_percent: 30 },
      { name: "Table Zone C", capacity: 40, x_percent: 30, y_percent: 55, width_percent: 40, height_percent: 30 },
    ],
  },
  {
    id: "blank",
    label: "Blank canvas",
    desc: "Start from scratch",
    sections: [],
  },
];

export function PresetWizard({ open, onClose, onPick }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const pick = async (preset) => {
    setBusy(true);
    try { await onPick?.(preset); } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Choose a starter layout"
      data-testid="preset-wizard"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full max-w-3xl bg-white rounded-sm shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="gt-label flex items-center gap-1.5"><Sparkles size={12} /> Starter layout</div>
            <h2 className="font-heading text-2xl font-black tracking-tight">Pick a starter arrangement</h2>
            <p className="text-sm text-[var(--gt-ink-3)] mt-1">You can drag, resize and rename everything after.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-sm border border-[var(--gt-line)] flex items-center justify-center hover:bg-[var(--gt-bg-surface)]"
            data-testid="preset-wizard-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={busy}
              onClick={() => pick(p)}
              className="text-left p-4 border border-[var(--gt-line)] rounded-sm bg-white hover:border-[var(--gt-ink)] transition-colors"
              data-testid={`preset-wizard-${p.id}`}
            >
              <div className="font-heading font-bold text-lg mb-1">{p.label}</div>
              <div className="text-xs text-[var(--gt-ink-3)]">{p.desc}</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] mt-2">
                {p.sections.length === 0 ? "Empty" : `${p.sections.filter(s => !s.is_decorative).length} sections · ${p.sections.filter(s => s.is_decorative).length} decor`}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
